import { observable, configure, action } from 'mobx';
import LocalModel from './localModel';
import util from 'preact-util';

configure({ enforceActions: 'always' });

class WorkSharingItemStore extends LocalModel {
    constructor() {
        super('workSharingItem', {
            namePlural: 'workSharingItems',
            namePluralReal: 'workSharingItems',
            sort: '-createdDate',
            limit: 50,
            api: {
                search: {
                    url: '/api/worksharingitems/',
                    params: {
                        limit: 50,
                    },
                },
                load: {
                    url: '/api/worksharingitems/',
                    params: {},
                },
                save: {
                    url: '/api/worksharingitems/',
                    params: {},
                },
                delete: {
                    url: '/api/worksharingitems/',
                    params: {},
                },
            },
        });
    }

    @observable workSharingItem = {};
    @observable workSharingItems = [];
    @observable newWorkSharingItem = {};
    @observable customers = [];
    @observable statusFilter = '';
    @observable priorityFilter = '';
    @observable customerFilter = null;
    @observable isLoading = false;

    statuses = ['open', 'in_progress', 'completed', 'cancelled'];
    priorities = ['low', 'normal', 'high', 'urgent'];
    jobTypes = ['consultation', 'vaccination', 'emergency', 'surgery', 'checkup', 'other'];

    @action
    resetNewItem() {
        this.newWorkSharingItem = {};
    }

    @action
    setStatusFilter(status) {
        this.statusFilter = status;
    }

    @action
    setPriorityFilter(priority) {
        this.priorityFilter = priority;
    }

    @action
    setCustomerFilter(customerId) {
        this.customerFilter = customerId;
    }

    async getItems(filters = {}) {
        this.updateKeyValue('isLoading', true);
        try {
            const params = { ...filters };
            if (this.statusFilter) {
                params.status = this.statusFilter;
            }
            if (this.priorityFilter) {
                params.priority = this.priorityFilter;
            }
            if (this.customerFilter) {
                params.customer = this.customerFilter;
            }

            const response = await util.fetchApi('/api/worksharingitems/', { method: 'GET' }, params);
            if (response.status === 200) {
                this.updateKeyValue('workSharingItems', response.data || []);
                this.updateKeyValue('customers', response.customers || []);
            }
            return response;
        } finally {
            this.updateKeyValue('isLoading', false);
        }
    }

    async getItem(id) {
        const response = await util.fetchApi(`/api/worksharingitems/${id}`, { method: 'GET' });
        if (response.status === 200) {
            this.updateKeyValue('workSharingItem', response.data || {});
        }
        return response;
    }

    async createItem(itemData) {
        const response = await util.fetchApi('/api/worksharingitems/', { method: 'POST' }, itemData);
        if (response.status === 201) {
            this.resetNewItem();
            // Refresh items list
            await this.getItems();
            return response.data;
        }
        return null;
    }

    async updateItem(id, updates) {
        const response = await util.fetchApi(`/api/worksharingitems/${id}`, { method: 'PATCH' }, updates);
        if (response.status === 200) {
            this.updateKeyValue('workSharingItem', response.data || {});
            // Update in list if exists
            const items = [...this.workSharingItems];
            const index = items.findIndex(item => item.id === id);
            if (index !== -1) {
                items[index] = response.data;
                this.updateKeyValue('workSharingItems', items);
            }
            return response.data;
        }
        return null;
    }

    async grabItem(id) {
        return this.updateItem(id, { grab: true });
    }

    async releaseItem(id) {
        return this.updateItem(id, { release: true });
    }

    async completeItem(id, outcome = '') {
        return this.updateItem(id, { complete: true, outcome });
    }

    async cancelItem(id) {
        return this.updateItem(id, { cancel: true });
    }

    async addNote(id, body) {
        const response = await util.fetchApi(`/api/worksharingitems/${id}/note`, { method: 'POST' }, { body });
        if (response.status === 201) {
            this.updateKeyValue('workSharingItem', response.data || {});
            // Update in list if exists
            const items = [...this.workSharingItems];
            const index = items.findIndex(item => item.id === id);
            if (index !== -1) {
                items[index] = response.data;
                this.updateKeyValue('workSharingItems', items);
            }
            return response.data;
        }
        return null;
    }

    async deleteItem(id) {
        return this.updateItem(id, { offline: 1 });
    }

    @action
    cleanup() {
        this.workSharingItem = {};
        this.workSharingItems = [];
        this.customers = [];
        this.statusFilter = '';
        this.priorityFilter = '';
        this.customerFilter = null;
        this.resetNewItem();
    }
}

const store = new WorkSharingItemStore();
export default store;
